import { useEffect } from "react";
import { Outlet, useSearchParams } from "react-router-dom";

import { useLoadSearch, useLoads } from "../api/queries";
import { linkTo } from "../api/links";
import type { Load } from "../api/types";
import {
  DetailPlaceholder,
  ListRow,
  LoadMore,
  MasterDetail,
  RowField,
  RowFields,
} from "../components/MasterDetail";
import { Badge, Empty, ErrorBox, Guid, Spinner, Time, shorten } from "../components/primitives";

const SCOPES = [
  { value: "", label: "active" },
  { value: "archived", label: "archived" },
  { value: "deleted", label: "deleted" },
  { value: "all", label: "all" },
];

const CHIPS = ["driver_guid", "carrier_guid"] as const;

/** Filters live in the URL so a view can be pasted into a ticket. */
export function LoadsPage() {
  const [params, setParams] = useSearchParams();
  const scope = params.get("scope") ?? "";
  const q = params.get("q") ?? "";
  const number = params.get("number");

  useEffect(() => {
    if (number === null) return;
    const next = new URLSearchParams(params);
    next.delete("number");
    if (number) next.set("q", number);
    setParams(next, { replace: true });
  }, [number, params, setParams]);

  const filters = {
    status: params.get("status") ?? undefined,
    driver_guid: params.get("driver_guid") ?? undefined,
    carrier_guid: params.get("carrier_guid") ?? undefined,
    archived: scope === "archived" ? "true" : scope === "" ? "false" : undefined,
    deleted: scope === "deleted" ? "true" : scope === "" ? "false" : undefined,
    limit: 50,
  };

  const list = useLoads(filters, !q);
  const search = useLoadSearch(q);
  const loads = q
    ? (search.data?.loads ?? [])
    : (list.data?.pages.flatMap((page) => page.loads) ?? []);
  const pending = q ? search.isPending : list.isPending;
  const error = q ? search.error : list.error;
  const success = q ? search.isSuccess : list.isSuccess;
  const hasNext = !q && list.hasNextPage;

  function update(key: string, value: string) {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  }

  return (
    <MasterDetail
      title="Loads"
      count={loads.length ? `${loads.length}${hasNext ? "+" : ""}` : undefined}
      filters={
        <>
          <input
            className="field-input flex-1 py-1 text-xs"
            placeholder="load number or guid…"
            key={q}
            defaultValue={q}
            onKeyDown={(event) => {
              if (event.key === "Enter") update("q", event.currentTarget.value.trim());
            }}
            spellCheck={false}
          />
          {!q && (
            <>
              <input
                className="field-input w-24 py-1 text-xs"
                placeholder="status"
                key={filters.status}
                defaultValue={filters.status ?? ""}
                onKeyDown={(event) => {
                  if (event.key === "Enter") update("status", event.currentTarget.value.trim());
                }}
                spellCheck={false}
              />
              <select
                className="field-select"
                value={scope}
                onChange={(event) => update("scope", event.target.value)}
              >
                {SCOPES.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {CHIPS.map((key) =>
                filters[key] ? (
                  <button
                    key={key}
                    type="button"
                    className="btn mono"
                    onClick={() => update(key, "")}
                  >
                    {key.replace("_guid", "")}: {shorten(filters[key])} ✕
                  </button>
                ) : null,
              )}
            </>
          )}
        </>
      }
      footer={
        q
          ? "Exact match on number (any letter case) or guid, across every scope."
          : "Newest first. A load that never applied has no row here — find it under Batches."
      }
      detail={<Outlet />}
    >
      {pending && <Spinner />}
      {error && <ErrorBox error={error} />}
      {success && loads.length === 0 && (
        <Empty>
          Nothing matches.
          <div className="mt-1 text-xs text-faint">
            Loads appear once an event applied — a load whose every event was rejected lives only in
            the journal and the inbox.
          </div>
        </Empty>
      )}

      {loads.map((load) => (
        <LoadListItem key={load.guid} load={load} />
      ))}

      <LoadMore
        hasNext={hasNext}
        loading={list.isFetchingNextPage}
        onClick={() => void list.fetchNextPage()}
      />
    </MasterDetail>
  );
}

function LoadListItem({ load }: { load: Load }) {
  return (
    <ListRow to={`/loads/${load.guid}`}>
      <div className="flex items-center gap-2">
        <span className="mono truncate text-sm text-strong">
          {load.number ?? load.guid.slice(0, 8)}
        </span>
        {load.status && <span className="truncate text-xs text-muted">{load.status}</span>}
        <span className="ml-auto flex shrink-0 items-center gap-1">
          {load.deleted && <Badge tone="bad">deleted</Badge>}
          {load.archived && <Badge tone="warn">arch</Badge>}
        </span>
      </div>
      <RowFields>
        <RowField label="version">
          <span className="mono text-muted">v{load.server_version}</span>
        </RowField>
        <RowField label="driver">
          <Guid value={load.driver_guid} to={linkTo.driver(load.driver_guid)} />
        </RowField>
        <RowField label="updated">
          <Time value={load.updated_at} />
        </RowField>
      </RowFields>
    </ListRow>
  );
}

export function LoadsPlaceholder() {
  return (
    <DetailPlaceholder>
      Pick a load to see every column it stores, its vehicles, the device feed and acks, and the
      whole journal grouped by the batch that carried each event.
    </DetailPlaceholder>
  );
}
